import { useState } from 'react';
import { useProductSecurityGroupsQuery } from '@/features/security-admin/hooks/useProductSecurityGroupsQuery';
import { PermissionGuard } from '@/features/auth';
import { Alert, Button } from '@shared/components';
import { ProductSecurityGroupDataTable } from './ProductSecurityGroupDataTable';
import { AttachSecurityGroupsModal } from './AttachSecurityGroupsModal';
import type { BackRef } from '../types/backRef';

interface Props {
  productId: string;
  /**
   * Product name supplied by the parent (`ProductDetail`) which already has
   * the product loaded. Passed as a prop instead of re-fetching here so the
   * `backRef` is always defined synchronously on the first render of the
   * DataTable — otherwise the groups query can resolve before a duplicate
   * `useProductQuery` here, leaving `backRef={undefined}` for the brief
   * window in which a user could click View and lose the back-nav context.
   */
  productName: string;
  /** Owner dealer ID from the SMR product — forwarded to the attach modal to
   *  gate the scoped-groups request and render the null-owner empty-state. */
  parentDealerId: string | null;
}

export function ProductSecurityGroupPanel({ productId, productName, parentDealerId }: Props) {
  const [showModal, setShowModal] = useState(false);
  const groupsQuery = useProductSecurityGroupsQuery(productId);
  const groups = groupsQuery.data ?? [];

  if (groupsQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading security groups...</p>;
  }

  if (groupsQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load security groups.</span>
      </Alert>
    );
  }

  const backRef: BackRef = {
    path: `/smr-admin/products/${productId}?tab=security-groups`,
    label: productName,
  };

  return (
    <div className="space-y-6">
      {groups.length === 0 ? (
        <p className="text-mp-muted text-sm">This product is not in any security groups.</p>
      ) : (
        <ProductSecurityGroupDataTable data={groups} backRef={backRef} productId={productId} />
      )}

      <div className="border-t border-mp-border pt-6 max-w-2xl">
        <h3 className="text-lg font-semibold mb-2">Security Groups</h3>
        <div className="flex items-start gap-4 justify-between">
          <p className="text-sm text-mp-muted">
            Security groups this product belongs to.
          </p>
          <PermissionGuard permission={{ resource: 'security_group', action: 'manage_products' }}>
            <div className="flex items-center gap-2 whitespace-nowrap">
              <Button size="sm" type="button" onClick={() => setShowModal(true)}>
                Attach to Security Group
              </Button>
            </div>
          </PermissionGuard>
        </div>
      </div>

      {showModal && (
        <AttachSecurityGroupsModal
          productId={productId}
          parentDealerId={parentDealerId}
          attachedGroupIds={groups.map((g) => g.id)}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
